// Service for extracting date information from emails using the existing AI prompt
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { extractTLDRFromEmail, createTLDR } from './tldrService';
import { trackTLDR, trackError } from '../utils/analytics';

// Firebase db is imported from '../firebase'

// Extract event information from email content using OpenAI
export const extractDatesFromEmail = async (emailBody, emailSubject, API_KEY, emailFrom = '') => {
  if (!emailBody || !API_KEY) {
    console.error('Missing required parameters for date extraction');
    return [];
  }
  
  // Check if this is from a school-related domain (either directly or forwarded)
  const isDirectSchoolEmail = emailFrom && (
    emailFrom.includes('notifications.seesaw.me') || 
    emailFrom.includes('arborschool.ae') ||
    emailFrom.includes('thearborschool.ae')
  );
  
  // Check if this is a forwarded school email by looking in the body
  const isForwardedSchoolEmail = emailBody && (
    emailBody.includes('notifications.seesaw.me') || 
    emailBody.includes('arborschool.ae') ||
    emailBody.includes('thearborschool.ae') ||
    emailBody.includes('From: notifications@notifications.seesaw.me') ||
    emailBody.includes('From: noreply@notifications.seesaw.me') ||
    emailBody.includes('From:') && emailBody.includes('@arborschool.ae') ||
    emailBody.includes('From:') && emailBody.includes('@thearborschool.ae')
  );
  
  const isSchoolEmail = isDirectSchoolEmail || isForwardedSchoolEmail;
  
  // Check if this is specifically from Seesaw for TLDR extraction
  const isSeesawEmail = isDirectSchoolEmail && emailFrom.includes('notifications.seesaw.me');
  
  try {
    console.log('Extracting dates from email with subject:', emailSubject);
    console.log('Email from:', emailFrom, 'Is school email:', isSchoolEmail, 'Is Seesaw email:', isSeesawEmail);
    
    // Create a prompt that combines the subject and body
    const combinedContent = `Email Subject: ${emailSubject || 'No subject'}\n\nEmail Body:\n${emailBody}`;
    
    // Get current date information for context
    const now = new Date();
    const dateInfo = {
      day: now.getDate(),
      month: now.getMonth() + 1, // JavaScript months are 0-indexed
      year: now.getFullYear(),
      dayOfWeek: now.getDay(),
      dayNames: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      monthNames: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
    };

    // Prepare messages for the AI model
    const systemContent = isSchoolEmail ? 
      // Special prompt for school-related emails
      `Extract all dates, times, events, locations, participants, and categories from the provided school-related email.

Today is ${dateInfo.monthNames[dateInfo.month-1]} ${dateInfo.day}, ${dateInfo.year} (${dateInfo.dayNames[dateInfo.dayOfWeek]}).

For dates:
- Handle relative terms like "today", "tomorrow", "next Monday", etc.
- When "next week" is mentioned without a specific day:
  * Interpret it as referring to next Monday by default
  * Example: "The meeting is next week" should be interpreted as next Monday
  * If any day is specified with "next week" (e.g., "next week on Wednesday"), use that specific day instead
- If a year is not mentioned, assume ${dateInfo.year}
- If a month is not mentioned, assume the current month (${dateInfo.month})
- If only a day of week is mentioned (e.g., "on Tuesday"), determine the next occurrence of that day
- If a date appears to be in the past for this year, consider if it might refer to next year

For times:
- Extract time information when available (e.g., "3:00 PM", "15:30", "morning", "noon", "afternoon")
- Handle time ranges (e.g., "from 9AM to 5PM")

For locations:
- Extract specific location information when mentioned (e.g., addresses, venue names, online/virtual)
- If no location is explicitly mentioned, set location to null

IMPORTANT - For context/description:
- Create a TLDR (Too Long; Didn't Read) summary instead of a full description
- The TLDR should be ONE concise sentence that captures the key action or purpose
- Keep it under 10-12 words when possible
- Focus on the core activity or requirement
- Examples of good TLDRs:
  * "Children will present sustainable toy creations and project writing."
  * "Whole primary school showcase. Parents can explore year group projects."
  * "Rehearsals ongoing. Costumes needed."
  * "Send a photo of child with father/father figure"
  * "Trip related to Sustainable Toymaker project. Details in Zenda."
  * "Child to teach something in class. Details sent by teacher."

For participants:
- Extract names of people the event is for (e.g., specific child's name if mentioned)
- If no specific person is mentioned, set the participant to null

For event categories:
- Since these are school emails, default to "school" category
- Only use "sport" if it's clearly a sports event
- Use "life" for social school events like parties or non-academic activities

Return a JSON object with a property called 'events' containing an array where each item has:
- day (numeric)
- month (numeric)
- year (numeric)
- hasTime (boolean) - whether a specific time was mentioned
- hour (numeric, 0-23) - if a time was mentioned
- minute (numeric, 0-59) - if a time was mentioned
- isAllDay (boolean) - true if no specific time was mentioned
- timeText (string) - the original time text, or general time of day mentioned
- eventName (string)
- description (string) - A TLDR summary for school emails
- location (string or null) - specific location information if mentioned, or null if no location was specified
- participant (string or null) - name of who the event is for/with, or null if not specified
- category (string) - usually "school" for these emails
- originalDateText (string) - the original date text as it appeared in the input`
      :
      // Original prompt for non-school emails
      `Extract all dates, times, events, locations, participants, and categories from the provided email.

Today is ${dateInfo.monthNames[dateInfo.month-1]} ${dateInfo.day}, ${dateInfo.year} (${dateInfo.dayNames[dateInfo.dayOfWeek]}).

For dates:
- Handle relative terms like "today", "tomorrow", "next Monday", etc.
- When "next week" is mentioned without a specific day:
  * Interpret it as referring to next Monday by default
  * Example: "The meeting is next week" should be interpreted as next Monday
  * If any day is specified with "next week" (e.g., "next week on Wednesday"), use that specific day instead
- If a year is not mentioned, assume ${dateInfo.year}
- If a month is not mentioned, assume the current month (${dateInfo.month})
- If only a day of week is mentioned (e.g., "on Tuesday"), determine the next occurrence of that day
- If a date appears to be in the past for this year, consider if it might refer to next year

For times:
- Extract time information when available (e.g., "3:00 PM", "15:30", "morning", "noon", "afternoon")
- Handle time ranges (e.g., "from 9AM to 5PM")

For locations:
- Extract specific location information when mentioned (e.g., addresses, venue names, online/virtual)
- If no location is explicitly mentioned, set location to null

For context:
- Create a short description that captures the context or purpose of the event based on surrounding text
- Limit descriptions to 1-2 sentences to keep them concise
- Include any requirements or details if mentioned
- Do NOT include location, participant names, or category information in the description, as these will be extracted separately

For participants:
- Extract names of people the event is for (e.g., "John's birthday", "meeting with Sarah", "appointment for the kids")
- If no specific person is mentioned, set the participant to null

For event categories:
- Identify the type of event based on contextual clues
- Categorize as one of: "work", "school", "sport", or "life"
- "work": work-related meetings, deadlines, professional events
- "school": classes, academic deadlines, school-related activities
- "sport": athletic activities, games, matches, workouts
- "life": social activities like dinners, parties, friend meetings, personal appointments
- Every event MUST be classified as one of these categories
- If unable to determine a clear category, set to "life"

Return a JSON object with a property called 'events' containing an array where each item has:
- day (numeric)
- month (numeric)
- year (numeric)
- hasTime (boolean) - whether a specific time was mentioned
- hour (numeric, 0-23) - if a time was mentioned
- minute (numeric, 0-59) - if a time was mentioned
- isAllDay (boolean) - true if no specific time was mentioned
- timeText (string) - the original time text, or general time of day mentioned
- eventName (string)
- description (string) - a concise description derived from the context
- location (string or null) - specific location information if mentioned, or null if no location was specified
- participant (string or null) - name of who the event is for/with, or null if not specified
- category (string) - one of: "work", "school", "sport", "life", "other"
- originalDateText (string) - the original date text as it appeared in the input`;

    const messages = [
      {
        role: "system",
        content: systemContent
      },
      {
        role: "user",
        content: combinedContent
      }
    ];
    
    // Call the OpenAI API
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEY}`
      },
      body: JSON.stringify({
        model: "gpt-4o",
        messages: messages,
        temperature: 0.2, // Lower temperature for more focused response
        max_tokens: 1500
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`API Error: ${errorData.error?.message || response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.choices || !data.choices[0] || !data.choices[0].message) {
      throw new Error('Invalid response format from AI model');
    }
    
    let aiResponse = data.choices[0].message.content.trim();
    
    // Remove markdown code block syntax if present
    if (aiResponse.startsWith('```json') || aiResponse.startsWith('```')) {
      // Find where the actual JSON starts
      const jsonStartIndex = aiResponse.indexOf('{');
      const jsonEndIndex = aiResponse.lastIndexOf('}');
      
      if (jsonStartIndex >= 0 && jsonEndIndex >= 0) {
        // Extract just the JSON part
        aiResponse = aiResponse.substring(jsonStartIndex, jsonEndIndex + 1);
      }
    }
    
    // Parse the JSON response
    let extractedEvents = [];
    try {
      // The response should now be a JSON object with an events array
      const parsedResponse = JSON.parse(aiResponse);
      
      // Check if we have a valid events array
      if (parsedResponse.events && Array.isArray(parsedResponse.events)) {
        extractedEvents = parsedResponse.events;
      } else if (Array.isArray(parsedResponse)) {
        // Fallback for backward compatibility (if it returns an array directly)
        console.warn('AI returned array directly instead of {events: [...]} format');
        extractedEvents = parsedResponse;
      } else {
        console.error('Expected events array but got:', parsedResponse);
        return [];
      }
      
      // Process each event
      const processedEvents = extractedEvents.map(event => {
        // Generate a unique ID for this event
        const id = 'email_' + new Date().getTime() + '_' + Math.floor(Math.random() * 1000);
        
        // Add processed time
        const now = new Date();
        
        // Format the event data according to the application's expectations
        return {
          id,
          eventName: event.eventName || 'Untitled Event',
          description: event.description || '',
          location: event.location || '',
          day: event.day || now.getDate(),
          month: event.month || (now.getMonth() + 1),
          year: event.year || now.getFullYear(),
          hour: event.hasTime ? (event.hour || 0) : 0,
          minute: event.hasTime ? (event.minute || 0) : 0,
          hasTime: !!event.hasTime,
          isAllDay: !!event.isAllDay,
          participants: Array.isArray(event.participants) ? event.participants : 
                       event.participant ? [event.participant] : [],
          participant: event.participant || null, // For compatibility with App.js format
          category: ['work', 'school', 'life', 'sport'].includes(event.category) ? event.category : 'life',
          source: 'email',
          emailSubject: emailSubject || 'No subject',
          // Add metadata
          created: now.toISOString(),
          lastModified: now.toISOString()
        };
      });
      
      console.log(`Extracted ${processedEvents.length} events from email`);
      return processedEvents;
    } catch (parseError) {
      console.error('Error parsing AI response:', parseError);
      console.error('Raw AI response:', aiResponse);
      return [];
    }
  } catch (error) {
    console.error('Error extracting dates from email:', error);
    return [];
  }
};

// Store extracted event in Firebase pending collection
export const storeExtractedEvent = async (event) => {
  try {
    const pendingEventsRef = collection(db, 'pendingEvents');
    const docRef = await addDoc(pendingEventsRef, {
      ...event,
      created: new Date().toISOString(),
      processed: false
    });
    
    console.log(`Event stored in pending collection with ID: ${docRef.id}`);
    return docRef.id;
  } catch (error) {
    console.error('Error storing extracted event:', error);
    throw error;
  }
};

// Update the email document with the extracted events
export const updateEmailWithEvents = async (emailFirestoreId, extractedEvents) => {
  try {
    if (!emailFirestoreId) {
      console.error('Missing email Firestore ID for update');
      return false;
    }
    
    const emailRef = doc(db, 'emails', emailFirestoreId);
    await updateDoc(emailRef, {
      extractedEvents: extractedEvents,
      lastProcessed: new Date()
    });
    
    console.log(`Email document updated with ${extractedEvents.length} events`);
    return true;
  } catch (error) {
    console.error('Error updating email with events:', error);
    return false;
  }
};

// Process TLDR for Seesaw emails
export const processTLDRForSeesawEmail = async (emailData, API_KEY, userId) => {
  const { body, subject, from, id: emailId, date } = emailData;
  console.log(`TLDR Processing: Email ID is ${emailId} for subject: ${subject}`);
  
  // Check if it's a direct Seesaw email
  const isDirectSeesawEmail = from && from.includes('notifications.seesaw.me');
  
  // Check if it's a forwarded Seesaw email by looking in the body
  const isForwardedSeesawEmail = body && (
    body.includes('notifications.seesaw.me') || 
    body.includes('From: notifications@notifications.seesaw.me') ||
    body.includes('From: noreply@notifications.seesaw.me') ||
    body.includes('From: do-not-reply@notifications.seesaw.me')
  );
  
  // Only process if it's from Seesaw (direct or forwarded)
  if (!isDirectSeesawEmail && !isForwardedSeesawEmail) {
    return null;
  }
  
  const tldrStartTime = Date.now();
  try {
    const emailType = isDirectSeesawEmail ? 'direct' : 'forwarded';
    console.log(`Processing TLDR for ${emailType} Seesaw email:`, subject);
    
    // Extract TLDR using the AI service
    const tldrResult = await extractTLDRFromEmail(body, subject, API_KEY);
    
    if (tldrResult && tldrResult.tldr) {
      // Create TLDR data object
      const tldrData = {
        summary: tldrResult.tldr,
        emailId: emailId, // Reference to the stored email for fetching body later
        emailSubject: subject,
        emailFrom: from,
        emailDate: new Date(date),
        category: tldrResult.category || 'announcement',
        priority: tldrResult.priority || 'medium',
        tags: tldrResult.tags || []
      };
      
      // Store TLDR in Firebase with userId
      const tldrId = await createTLDR(tldrData, userId);
      console.log(`TLDR created for Seesaw email: ${tldrId}`);
      
      // Track successful TLDR generation
      const processingTime = Date.now() - tldrStartTime;
      trackTLDR.generate(
        body.length,
        tldrResult.tldr.length,
        processingTime,
        true
      );
      
      return {
        ...tldrData,
        id: tldrId
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error processing TLDR for Seesaw email:', error);
    
    // Track failed TLDR generation
    const processingTime = Date.now() - tldrStartTime;
    trackTLDR.generate(body.length, 0, processingTime, false);
    trackError('tldr_generation_failed', error.message);
    
    return null;
  }
};